import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { bomberosMaterialMenorAPI, bomberosCompaniaAPI, bomberosSolicitudesBajaAPI, bomberosPersonalAPI } from '../../lib/api';
import { useBomberosPermisos } from '../../hooks/useBomberosPermisos';
import { BomberosHeader } from '../../components/BomberosHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../../components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../../components/ui/popover';
import { toast } from 'sonner';
import {
    HardHat,
    Plus,
    Search,
    ArrowLeft,
    Package,
    User,
    AlertTriangle,
    CheckCircle,
    Eye,
    Edit,
    Trash2,
    Upload,
    FileText,
    Loader2,
    X,
    Clock,
    UserPlus,
    UserMinus,
    Check,
    ChevronsUpDown,
} from 'lucide-react';

const tipoLabels = {
    casco: 'Casco',
    chaqueton: 'Chaquetón',
    pantalon: 'Pantalón',
    botas: 'Botas',
    guantes: 'Guantes',
    capucha: 'Capucha',
    equipo_respiracion: 'ERA',
    linterna: 'Linterna',
    radio: 'Radio',
};

export default function BomberosEPPPage() {
    const navigate = useNavigate();
    const { tienePermiso } = useBomberosPermisos();
    const [loading, setLoading] = useState(true);
    const [materiales, setMateriales] = useState([]);
    const [companias, setCompanias] = useState([]);
    const [filteredMateriales, setFilteredMateriales] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCompania, setFilterCompania] = useState('all');
    const [filterTipo, setFilterTipo] = useState('all');
    const [filterEstado, setFilterEstado] = useState('all');
    
    // Estado para diálogo de solicitud de baja
    const [showBajaDialog, setShowBajaDialog] = useState(false);
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const [bajaForm, setBajaForm] = useState({
        motivo: '',
        observaciones: '',
    });
    const [evidenciaFile, setEvidenciaFile] = useState(null);
    const [evidenciaUrl, setEvidenciaUrl] = useState(null);
    const [uploadingEvidencia, setUploadingEvidencia] = useState(false);
    const [submittingBaja, setSubmittingBaja] = useState(false);
    
    // Estado para diálogo de asignación
    const [showAsignarDialog, setShowAsignarDialog] = useState(false);
    const [bomberos, setBomberos] = useState([]);
    const [bomberosFiltered, setBomberosFiltered] = useState([]);
    const [searchBombero, setSearchBombero] = useState('');
    const [selectedBombero, setSelectedBombero] = useState(null);
    const [openBomberoCombobox, setOpenBomberoCombobox] = useState(false);
    const [submittingAsignacion, setSubmittingAsignacion] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        filterMateriales();
    }, [materiales, searchTerm, filterCompania, filterTipo, filterEstado]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [materialesRes, companiasRes, bomberosRes] = await Promise.all([
                bomberosMaterialMenorAPI.getAll(),
                bomberosCompaniaAPI.getAll(),
                bomberosPersonalAPI.getAll({ estado: 'activo' }),
            ]);
            setMateriales(materialesRes.data);
            setCompanias(companiasRes.data);
            setBomberos(bomberosRes.data);
        } catch (err) {
            console.error('Error loading data:', err);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const filterMateriales = () => {
        let filtered = [...materiales];

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(m =>
                m.codigo?.toLowerCase().includes(term) ||
                m.bombero_nombre?.toLowerCase().includes(term) ||
                m.numero_serie?.toLowerCase().includes(term)
            );
        }

        if (filterCompania !== 'all') {
            filtered = filtered.filter(m => m.compania_id === filterCompania);
        }

        if (filterTipo !== 'all') {
            filtered = filtered.filter(m => m.tipo === filterTipo);
        }

        if (filterEstado !== 'all') {
            filtered = filtered.filter(m => m.estado === filterEstado);
        }

        setFilteredMateriales(filtered);
    };

    const handleOpenBajaDialog = (material) => {
        setSelectedMaterial(material);
        setBajaForm({ motivo: '', observaciones: '' });
        setEvidenciaFile(null);
        setEvidenciaUrl(null);
        setShowBajaDialog(true);
    };
    
    const handleOpenAsignarDialog = (material) => {
        setSelectedMaterial(material);
        setSelectedBombero(null);
        setSearchBombero('');
        setBomberosFiltered(bomberos);
        setShowAsignarDialog(true);
    };
    
    const handleSearchBombero = useCallback((value) => {
        setSearchBombero(value);
        if (!value.trim()) {
            setBomberosFiltered(bomberos);
        } else {
            const term = value.toLowerCase();
            setBomberosFiltered(bomberos.filter(b => {
                const nombres = b.nombres?.toLowerCase() || '';
                const apellidoPaterno = b.apellido_paterno?.toLowerCase() || '';
                const apellidoMaterno = b.apellido_materno?.toLowerCase() || '';
                const rut = b.rut?.toLowerCase() || '';
                const nombreCompleto = `${nombres} ${apellidoPaterno} ${apellidoMaterno}`.toLowerCase();
                
                return nombres.includes(term) ||
                    apellidoPaterno.includes(term) ||
                    apellidoMaterno.includes(term) ||
                    rut.includes(term) ||
                    nombreCompleto.includes(term);
            }));
        }
    }, [bomberos]);
    
    const handleAsignarBombero = async () => {
        if (!selectedBombero) {
            toast.error('Debe seleccionar un bombero');
            return;
        }
        
        setSubmittingAsignacion(true);
        try {
            await bomberosMaterialMenorAPI.asignar(selectedMaterial.id, {
                bombero_id: selectedBombero.id
            });
            toast.success(`EPP asignado a ${selectedBombero.nombres} ${selectedBombero.apellido_paterno}`);
            setShowAsignarDialog(false);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al asignar EPP');
        } finally {
            setSubmittingAsignacion(false);
        }
    };
    
    const handleDesasignarBombero = async (material) => {
        if (!window.confirm(`¿Está seguro de desasignar el EPP ${material.codigo} de ${material.bombero_nombre}?`)) {
            return;
        }
        
        try {
            await bomberosMaterialMenorAPI.desasignar(material.id);
            toast.success('EPP desasignado correctamente');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al desasignar EPP');
        }
    };

    const handleUploadEvidencia = async (file) => {
        if (!file) return;
        
        setUploadingEvidencia(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await bomberosSolicitudesBajaAPI.uploadEvidencia(formData);
            setEvidenciaUrl(response.data.url);
            setEvidenciaFile(file);
            toast.success('Evidencia subida correctamente');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al subir evidencia');
            setEvidenciaFile(null);
        } finally {
            setUploadingEvidencia(false);
        }
    };

    const handleSubmitBaja = async () => {
        if (!bajaForm.motivo.trim()) {
            toast.error('Debe indicar el motivo de la baja');
            return;
        }
        if (!evidenciaUrl) {
            toast.error('Debe adjuntar el acta de baja como evidencia');
            return;
        }

        setSubmittingBaja(true);
        try {
            await bomberosSolicitudesBajaAPI.create({
                material_menor_id: selectedMaterial.id,
                motivo: bajaForm.motivo,
                documento_evidencia_url: evidenciaUrl,
                observaciones: bajaForm.observaciones || null,
            });
            toast.success('Solicitud de baja enviada. Pendiente de validación.');
            setShowBajaDialog(false);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al enviar solicitud');
        } finally {
            setSubmittingBaja(false);
        }
    };

    const getEstadoConfig = (estado) => {
        switch (estado) {
            case 'disponible':
                return { color: 'bg-green-100 text-green-700 border-green-300', label: 'Disponible' };
            case 'asignado':
                return { color: 'bg-blue-100 text-blue-700 border-blue-300', label: 'Asignado' };
            case 'mantenimiento':
                return { color: 'bg-yellow-100 text-yellow-700 border-yellow-300', label: 'Mantenimiento' };
            case 'baja':
                return { color: 'bg-red-100 text-red-700 border-red-300', label: 'Baja' };
            case 'pendiente_baja':
                return { color: 'bg-orange-100 text-orange-700 border-orange-300', label: 'Pend. Baja' };
            default:
                return { color: 'bg-gray-100 text-gray-700 border-gray-300', label: estado };
        }
    };

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    // Calcular stats
    const stats = {
        total: materiales.length,
        disponible: materiales.filter(m => m.estado === 'disponible').length,
        asignado: materiales.filter(m => m.estado === 'asignado').length,
        mantenimiento: materiales.filter(m => m.estado === 'mantenimiento').length,
        porVencer: materiales.filter(m => m.dias_para_vencimiento !== null && m.dias_para_vencimiento <= 30).length,
    };

    return (
        <>
            <BomberosHeader />
            <div className="space-y-6 p-6 pt-20 md:pt-20">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/dashboard')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <HardHat className="h-6 w-6" />
                            Material Menor y EPP
                        </h1>
                        <p className="text-muted-foreground">{filteredMateriales.length} elementos registrados</p>
                    </div>
                </div>
                {tienePermiso('crear_epp') && (
                    <Button onClick={() => navigate('/bomberos/epp/nuevo')} data-testid="nuevo-epp-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Nuevo EPP
                    </Button>
                )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                <Card className="border">
                    <CardContent className="pt-4 pb-4 text-center">
                        <Package className="h-5 w-5 mx-auto mb-1 text-gray-500" />
                        <p className="text-2xl font-bold">{stats.total}</p>
                        <p className="text-xs text-muted-foreground">Total</p>
                    </CardContent>
                </Card>
                <Card className="border border-green-200">
                    <CardContent className="pt-4 pb-4 text-center">
                        <CheckCircle className="h-5 w-5 mx-auto mb-1 text-green-500" />
                        <p className="text-2xl font-bold text-green-600">{stats.disponible}</p>
                        <p className="text-xs text-muted-foreground">Disponible</p>
                    </CardContent>
                </Card>
                <Card className="border border-blue-200">
                    <CardContent className="pt-4 pb-4 text-center">
                        <User className="h-5 w-5 mx-auto mb-1 text-blue-500" />
                        <p className="text-2xl font-bold text-blue-600">{stats.asignado}</p>
                        <p className="text-xs text-muted-foreground">Asignado</p>
                    </CardContent>
                </Card>
                <Card className="border border-yellow-200">
                    <CardContent className="pt-4 pb-4 text-center">
                        <HardHat className="h-5 w-5 mx-auto mb-1 text-yellow-500" />
                        <p className="text-2xl font-bold text-yellow-600">{stats.mantenimiento}</p>
                        <p className="text-xs text-muted-foreground">Mantenimiento</p>
                    </CardContent>
                </Card>
                <Card className="border border-red-200">
                    <CardContent className="pt-4 pb-4 text-center">
                        <AlertTriangle className="h-5 w-5 mx-auto mb-1 text-red-500" />
                        <p className="text-2xl font-bold text-red-600">{stats.porVencer}</p>
                        <p className="text-xs text-muted-foreground">Por Vencer</p>
                    </CardContent>
                </Card>
            </div>

            {/* Filters */}
            <Card className="border-2">
                <CardContent className="pt-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Buscar por código, serie o bombero asignado..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                                data-testid="search-input"
                            />
                        </div>
                        <Select value={filterCompania} onValueChange={setFilterCompania}>
                            <SelectTrigger className="w-full md:w-48" data-testid="filter-compania">
                                <SelectValue placeholder="Compañía" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todas las Compañías</SelectItem>
                                {companias.map(c => (
                                    <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={filterTipo} onValueChange={setFilterTipo}>
                            <SelectTrigger className="w-full md:w-40" data-testid="filter-tipo">
                                <SelectValue placeholder="Tipo" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los Tipos</SelectItem>
                                {Object.entries(tipoLabels).map(([key, label]) => (
                                    <SelectItem key={key} value={key}>{label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={filterEstado} onValueChange={setFilterEstado}>
                            <SelectTrigger className="w-full md:w-40" data-testid="filter-estado">
                                <SelectValue placeholder="Estado" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                <SelectItem value="disponible">Disponible</SelectItem>
                                <SelectItem value="asignado">Asignado</SelectItem>
                                <SelectItem value="mantenimiento">Mantenimiento</SelectItem>
                                <SelectItem value="baja">Baja</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card className="border-2">
                <CardContent className="p-0 overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Código</TableHead>
                                <TableHead>Tipo</TableHead>
                                <TableHead>Marca / Modelo</TableHead>
                                <TableHead>Talla</TableHead>
                                <TableHead>Estado</TableHead>
                                <TableHead>Asignado a</TableHead>
                                <TableHead>Compañía</TableHead>
                                <TableHead>Vencimiento</TableHead>
                                <TableHead className="text-right">Acciones</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredMateriales.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                                        No se encontró material con los filtros aplicados
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredMateriales.map((material) => {
                                    const estadoConfig = getEstadoConfig(material.estado);
                                    const isExpiring = material.dias_para_vencimiento !== null && material.dias_para_vencimiento <= 30;

                                    return (
                                        <TableRow key={material.id} data-testid={`row-${material.codigo}`}>
                                            <TableCell className="font-mono text-sm font-medium">{material.codigo}</TableCell>
                                            <TableCell>{tipoLabels[material.tipo] || material.tipo}</TableCell>
                                            <TableCell>
                                                <div className="text-sm">
                                                    <p>{material.marca || '-'}</p>
                                                    {material.modelo && (
                                                        <p className="text-muted-foreground text-xs">{material.modelo}</p>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>{material.talla || '-'}</TableCell>
                                            <TableCell>
                                                <Badge className={estadoConfig.color}>
                                                    {estadoConfig.label}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {material.bombero_nombre ? (
                                                    <div className="flex items-center gap-2">
                                                        <User className="h-4 w-4 text-blue-500" />
                                                        <span className="text-sm font-medium">{material.bombero_nombre}</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted-foreground text-sm">Sin asignar</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-sm">{material.compania_nombre || '-'}</TableCell>
                                            <TableCell>
                                                {material.fecha_vencimiento ? (
                                                    <div className={`flex items-center gap-1 ${isExpiring ? 'text-red-600' : ''}`}>
                                                        {isExpiring && <AlertTriangle className="h-3 w-3" />}
                                                        <span className="text-sm">{material.fecha_vencimiento}</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted-foreground">-</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex justify-end gap-1">
                                                    {/* Botón Asignar/Desasignar */}
                                                    {tienePermiso('asignar_epp') && material.estado !== 'baja' && material.estado !== 'pendiente_baja' && (
                                                        material.bombero_id ? (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleDesasignarBombero(material)}
                                                                title="Desasignar bombero"
                                                                className="text-orange-600 hover:text-orange-700"
                                                            >
                                                                <UserMinus className="h-4 w-4" />
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleOpenAsignarDialog(material)}
                                                                title="Asignar a bombero"
                                                                className="text-blue-600 hover:text-blue-700"
                                                            >
                                                                <UserPlus className="h-4 w-4" />
                                                            </Button>
                                                        )
                                                    )}
                                                    {tienePermiso('editar_epp') && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => navigate(`/bomberos/epp/${material.id}/editar`)}
                                                        >
                                                            <Edit className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    {tienePermiso('eliminar_epp') && material.estado !== 'baja' && material.estado !== 'pendiente_baja' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-destructive hover:text-destructive"
                                                            onClick={() => handleOpenBajaDialog(material)}
                                                            title="Solicitar baja"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    {material.estado === 'pendiente_baja' && (
                                                        <Badge variant="outline" className="text-orange-600 border-orange-300">
                                                            <Clock className="h-3 w-3 mr-1" />
                                                            Validando
                                                        </Badge>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Resumen por Tipo */}
            <Card className="border-2">
                <CardContent className="pt-6">
                    <h3 className="font-semibold mb-4">Resumen por Tipo de EPP</h3>
                    <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3">
                        {Object.entries(tipoLabels).map(([key, label]) => {
                            const count = materiales.filter(m => m.tipo === key).length;
                            const asignados = materiales.filter(m => m.tipo === key && m.estado === 'asignado').length;
                            return (
                                <div key={key} className="text-center p-3 rounded-lg bg-muted/50">
                                    <p className="text-lg font-bold">{count}</p>
                                    <p className="text-xs text-muted-foreground">{label}</p>
                                    <p className="text-xs text-blue-600">{asignados} asig.</p>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Diálogo de Solicitud de Baja */}
            <Dialog open={showBajaDialog} onOpenChange={setShowBajaDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Trash2 className="h-5 w-5 text-destructive" />
                            Solicitar Baja de EPP
                        </DialogTitle>
                        <DialogDescription>
                            {selectedMaterial && (
                                <span>
                                    Código: <strong>{selectedMaterial.codigo}</strong> - {tipoLabels[selectedMaterial.tipo] || selectedMaterial.tipo}
                                </span>
                            )}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Motivo de la Baja *</Label>
                            <Select 
                                value={bajaForm.motivo} 
                                onValueChange={(v) => setBajaForm(prev => ({ ...prev, motivo: v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccione el motivo" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="desgaste_normal">Desgaste por uso normal</SelectItem>
                                    <SelectItem value="daño_operacional">Daño en operación</SelectItem>
                                    <SelectItem value="vencimiento">Vencimiento de vida útil</SelectItem>
                                    <SelectItem value="extravio">Extravío</SelectItem>
                                    <SelectItem value="obsolescencia">Obsolescencia técnica</SelectItem>
                                    <SelectItem value="otro">Otro motivo</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Observaciones</Label>
                            <Textarea
                                value={bajaForm.observaciones}
                                onChange={(e) => setBajaForm(prev => ({ ...prev, observaciones: e.target.value }))}
                                placeholder="Detalles adicionales sobre la baja..."
                                rows={3}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Acta de Baja (Evidencia) *</Label>
                            <p className="text-xs text-muted-foreground mb-2">
                                Suba el documento escaneado del acta de baja firmada
                            </p>
                            
                            {!evidenciaFile ? (
                                <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary transition-colors">
                                    <input
                                        type="file"
                                        accept="image/*,.pdf"
                                        onChange={(e) => handleUploadEvidencia(e.target.files[0])}
                                        className="hidden"
                                        id="evidencia-baja"
                                        disabled={uploadingEvidencia}
                                    />
                                    <label htmlFor="evidencia-baja" className="cursor-pointer">
                                        {uploadingEvidencia ? (
                                            <div className="flex flex-col items-center gap-2">
                                                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                                                <span className="text-sm text-muted-foreground">Subiendo...</span>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center gap-2">
                                                <Upload className="h-8 w-8 text-muted-foreground" />
                                                <span className="text-sm text-muted-foreground">
                                                    Clic para seleccionar archivo
                                                </span>
                                            </div>
                                        )}
                                    </label>
                                </div>
                            ) : (
                                <div className="flex items-center gap-3 p-3 bg-green-50 border-2 border-green-200 rounded-lg">
                                    <FileText className="h-8 w-8 text-green-600" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm truncate">{evidenciaFile.name}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {(evidenciaFile.size / 1024).toFixed(1)} KB
                                        </p>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => {
                                            setEvidenciaFile(null);
                                            setEvidenciaUrl(null);
                                        }}
                                        className="text-destructive shrink-0"
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                        </div>

                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <p className="text-sm text-amber-800">
                                <AlertTriangle className="h-4 w-4 inline mr-1" />
                                Esta solicitud será enviada para validación. Un usuario con permiso de validación deberá aprobarla antes de que el EPP sea dado de baja definitivamente.
                            </p>
                        </div>
                    </div>

                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setShowBajaDialog(false)}>
                            Cancelar
                        </Button>
                        <Button 
                            onClick={handleSubmitBaja} 
                            disabled={submittingBaja || !bajaForm.motivo || !evidenciaUrl}
                            className="bg-destructive hover:bg-destructive/90"
                        >
                            {submittingBaja ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Enviando...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Enviar Solicitud
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Diálogo de Asignación de Bombero */}
            <Dialog open={showAsignarDialog} onOpenChange={setShowAsignarDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UserPlus className="h-5 w-5 text-blue-600" />
                            Asignar EPP a Bombero
                        </DialogTitle>
                        <DialogDescription>
                            {selectedMaterial && (
                                <span>
                                    Código: <strong>{selectedMaterial.codigo}</strong> - {tipoLabels[selectedMaterial.tipo] || selectedMaterial.tipo}
                                </span>
                            )}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Seleccionar Bombero *</Label>
                            <Popover open={openBomberoCombobox} onOpenChange={setOpenBomberoCombobox}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={openBomberoCombobox}
                                        className="w-full justify-between"
                                    >
                                        {selectedBombero ? (
                                            <span className="flex items-center gap-2">
                                                <User className="h-4 w-4" />
                                                {selectedBombero.nombres} {selectedBombero.apellido_paterno}
                                            </span>
                                        ) : (
                                            "Buscar bombero..."
                                        )}
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-full p-0" align="start">
                                    <Command>
                                        <CommandInput 
                                            placeholder="Buscar por nombre o RUT..." 
                                            value={searchBombero}
                                            onValueChange={handleSearchBombero}
                                        />
                                        <CommandList>
                                            <CommandEmpty>No se encontraron bomberos.</CommandEmpty>
                                            <CommandGroup className="max-h-64 overflow-auto">
                                                {bomberosFiltered.map((bombero) => (
                                                    <CommandItem
                                                        key={bombero.id}
                                                        value={`${bombero.nombres} ${bombero.apellido_paterno} ${bombero.apellido_materno || ''} ${bombero.rut}`}
                                                        onSelect={() => {
                                                            setSelectedBombero(bombero);
                                                            setOpenBomberoCombobox(false);
                                                        }}
                                                    >
                                                        <Check
                                                            className={`mr-2 h-4 w-4 ${
                                                                selectedBombero?.id === bombero.id ? "opacity-100" : "opacity-0"
                                                            }`}
                                                        />
                                                        <div className="flex flex-col">
                                                            <span className="font-medium">
                                                                {bombero.nombres} {bombero.apellido_paterno} {bombero.apellido_materno || ''}
                                                            </span>
                                                            <span className="text-xs text-muted-foreground">
                                                                {bombero.rut} - {bombero.compania_nombre || 'Sin compañía'}
                                                            </span>
                                                        </div>
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>

                        {selectedBombero && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <div className="flex items-center gap-3">
                                    <User className="h-10 w-10 text-blue-600 bg-blue-100 rounded-full p-2" />
                                    <div>
                                        <p className="font-medium">{selectedBombero.nombres} {selectedBombero.apellido_paterno}</p>
                                        <p className="text-sm text-muted-foreground">{selectedBombero.rut}</p>
                                        <p className="text-xs text-blue-600">{selectedBombero.compania_nombre}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setShowAsignarDialog(false)}>
                            Cancelar
                        </Button>
                        <Button 
                            onClick={handleAsignarBombero} 
                            disabled={submittingAsignacion || !selectedBombero}
                        >
                            {submittingAsignacion ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Asignando...
                                </>
                            ) : (
                                <>
                                    <UserPlus className="h-4 w-4 mr-2" />
                                    Asignar
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
        </>
    );
}
